import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  applyServerGridTextSearchToBody,
  buildPassengerBulkSelectionIdsSearchBody,
  BulkGridSelectionModeEnum,
  isEntityServerSidePaginated,
  isSearchboxPausedSF,
  lastAppliedSearchCriteriaAF,
  Log,
  PassengerApi,
  tableColumnsSortingAF,
  tableSearchAF,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { usePageContext } from "../contexts";
import { useComposedSearchCriteria } from "./search";

/** Matches BE `MAX_SEARCH_PAGE_SIZE` for search-ids `take`. */
const SERVER_BULK_SELECTION_MAX_TAKE = 500;

export const useFetchServerPaginatedBulkSelectionIds = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const columnSorting = useRecoilValue(tableColumnsSortingAF(entity));
  const searchValue = useRecoilValue(tableSearchAF(entity));
  const isSearchBoxPaused = useRecoilValue(isSearchboxPausedSF(entity));
  const lastAppliedSearchCriteria = useRecoilValue(
    lastAppliedSearchCriteriaAF(entity),
  );
  const { getDynamicFormsComposedCriteria } = useComposedSearchCriteria(entity);

  const fetchBulkSelectionIds = useEvent(
    async ({
      mode,
      rowCount = 0,
      filterByField,
      filterByValue,
    }: {
      mode: BulkGridSelectionModeEnum;
      rowCount?: number;
      filterByField?: string;
      filterByValue?: string | number | boolean | null;
    }): Promise<number[] | undefined> => {
      if (!isEntityServerSidePaginated(entity)) {
        return undefined;
      }

      if (entity !== "passenger") {
        Log.warn(
          `Server bulk selection ids fetch is not implemented for ${entity}`,
        );

        return [];
      }

      if (mode === BulkGridSelectionModeEnum.Page) {
        return undefined;
      }

      try {
        const where =
          lastAppliedSearchCriteria ??
          (() => {
            const criteria = getDynamicFormsComposedCriteria();
            const bodyForText = { where: criteria };
            applyServerGridTextSearchToBody({
              body: bodyForText,
              entity,
              searchValue: isSearchBoxPaused ? undefined : searchValue,
            });
            return bodyForText.where;
          })();

        const cappedRowCount = Math.min(
          Math.max(0, Math.floor(rowCount)),
          SERVER_BULK_SELECTION_MAX_TAKE,
        );
        const body = buildPassengerBulkSelectionIdsSearchBody({
          where,
          columnSorting,
          mode,
          rowCount: cappedRowCount,
          filterByField,
          filterByValue,
        });
        const result = await PassengerApi.searchPassengerIdsByCriteria(body);

        if (!result?.success || !result.data) {
          if (result?.error) {
            warnAndToastSimpleResult(result, t);
          }

          return undefined;
        }

        return result.data.map(row => row.id);
      } catch (error) {
        Log.errorAndToast(t("errors.failedToFetchTableRows"), error);

        return undefined;
      }
    },
  );

  return { fetchBulkSelectionIds };
};
